import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/CustomersService';
import { useState } from 'react';
import { CustomerStatus } from '../types';

export const useCustomers = (externalStatusFilter?: CustomerStatus | null) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [statusFilter, setStatusFilter] = useState<CustomerStatus | null>(null);
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});
    
    // Use external filter if provided, otherwise use internal filter
    const activeStatusFilter = externalStatusFilter !== undefined ? externalStatusFilter : statusFilter;
    
    const sort = { column: 'created', direction: 'desc' as const };
    
    // INFINITE QUERY
    const customersQuery = useInfiniteQuery({
        queryKey: ['customers', keyword, activeStatusFilter, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({ limit, sort, nextToken: pageParam, statusFilter: activeStatusFilter }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customers'] });
        }
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({ queryKey: ['customers'] });
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const handleFilterByStatus = (status: CustomerStatus | null) => {
        setStatusFilter(status);
    };

    const allItems = customersQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: customersQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: customersQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        handleFilterByStatus,
        statusFilter,
        deleteMutation
    };
};